import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Costo, TipoMoneda } from '@/types/costo';
import { importeImpreso } from './calculo';

interface MonedaImpresionProps {
  costo: Costo;
  /** Cotización del dólar del presupuesto: sin ella el ítem sale en su moneda. */
  cotizacionDolar?: number | null;
  canEdit?: boolean;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** null vuelve a imprimir el ítem en su propia moneda. */
  onCambiar: (monedaImpresion: TipoMoneda | null) => void;
}

const formatearMonto = (monto: number, moneda: TipoMoneda): string =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(monto);

/**
 * Moneda en la que el ítem sale en el PDF. Por defecto es la del ítem; si es
 * otra, muestra el importe convertido con la cotización del dólar (o avisa que
 * falta, y entonces sale en su moneda).
 */
export const MonedaImpresion: React.FC<MonedaImpresionProps> = ({
  costo,
  cotizacionDolar = null,
  canEdit = false,
  disabled = false,
  onOpenChange,
  onCambiar,
}) => {
  const monedaImpresion = costo.monedaImpresion ?? costo.moneda;
  const enOtraMoneda = monedaImpresion !== costo.moneda;

  // Sin permiso de edición solo se informa cuando sale en otra moneda.
  if (!canEdit && !enOtraMoneda) return null;

  const impreso = importeImpreso(costo, cotizacionDolar);

  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <span className="text-muted-foreground/80">imprime en</span>
      {canEdit ? (
        <Select
          value={monedaImpresion}
          onValueChange={(moneda: TipoMoneda) => onCambiar(moneda === costo.moneda ? null : moneda)}
          onOpenChange={onOpenChange}
          disabled={disabled}
        >
          <SelectTrigger
            size="sm"
            aria-label={`Moneda de impresión de ${costo.nombre}`}
            className="h-6 border-0 px-2 py-0 text-xs font-semibold shadow-none"
            title="En el PDF, otra moneda se convierte con la cotización del dólar"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ARS">ARS</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <span className="font-semibold">{monedaImpresion}</span>
      )}
      {enOtraMoneda &&
        (impreso.moneda === monedaImpresion ? (
          <span
            className="font-medium text-foreground"
            title="Así sale en el PDF, convertido con la cotización del dólar"
          >
            {formatearMonto(impreso.importe, impreso.moneda)}
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-amber-700"
            title={`Sale en ${costo.moneda} hasta que se cargue la cotización del dólar`}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            falta la cotización
          </span>
        ))}
    </span>
  );
};
